import { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Button, Grid, Paper, Stack } from '@mui/material';
import { TextField } from '@mui/material';
import { AddCircleOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

// Hooks and services
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { upsertContract } from '../../../services/contract.service';

// Util
import { shortDateFormat } from '../../../utils/Format';

// Types
import { Contract } from '../../../types/Contract';
import { upsertContractModel } from '../../../requestModels/upsertContractModel';

// Common
import CustomModal from '../../common/withModal';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const emptyValue = {
  name: '',
  description: '',
  startDate: dayjs(new Date()),
  endDate: undefined,
  id: '',
} as upsertContractModel;

interface RoleFormProps {
  model?: upsertContractModel;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

export default function RoleForm(props: RoleFormProps) {
  const { notifyMessage } = useNotifyMessage();

  const validationSchema = () => {
    return Yup.object<Contract>({
      name: Yup.string().required('Name is required'),
      startDate: Yup.date().required('Start date is required'),
    });
  };
  const handleSubmit = async (values) => {
    const response = await upsertContract(values);

    if (response.status === 200) {
      notifyMessage('Contract created', 'success');
      props.setIsOpen(false);
    } else if (response.status >= 400 && response.status < 500) notifyMessage(response.data, 'error');
  };

  const formik = useFormik({
    initialValues: props.model || emptyValue,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (props.isOpen) setTimeout(() => document.getElementById('txt-name')?.focus(), 0);
  }, [props.isOpen]);

  return (
    <>
      <Stack direction='row' spacing={1} justifyContent='flex-end'>
        <Button
          onClick={() => {
            props.setIsOpen(true);
          }}
          variant='contained'
          size='small'
          sx={tablePrimaryButtonSx}
          endIcon={<AddCircleOutlined style={{ fontSize: 20 }} />}
        >
          Add Contract
        </Button>
      </Stack>
      <CustomModal
        maxWidth='lg'
        title='Add Contract'
        isOpen={props.isOpen}
        isValid={formik.isValid}
        setIsOpen={props.setIsOpen}
        onConfirm={formik.handleSubmit}
      >
        <Paper elevation={3} style={{ overflowX: 'auto' }}>
          <form onSubmit={formik.handleSubmit} style={{ width: 'auto', height: 'auto', padding: 20 }}>
            <Grid container spacing={3}>
              <Grid xs={12} item>
                <TextField
                  id='txt-name'
                  name='name'
                  label='Name'
                  value={formik.values?.name ?? ''}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder='Name'
                  variant='standard'
                  size='small'
                  fullWidth
                  required
                  margin='dense'
                />
              </Grid>
              <Grid xs={12} item>
                <TextField
                  id='txt-description'
                  name='description'
                  label='Description'
                  value={formik.values?.description ?? ''}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  placeholder='Description'
                  variant='outlined'
                  size='medium'
                  multiline
                  rows={4}
                  fullWidth
                  margin='dense'
                />
              </Grid>
              <Grid xs={12} item>
                <DatePicker
                  name='startDate'
                  label='Start Date'
                  value={formik.values.startDate}
                  onChange={(newValue) => {
                    formik.setFieldValue('startDate', newValue);
                    formik.setFieldTouched('startDate', true);
                  }}
                  format={shortDateFormat}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: formik.touched.startDate && Boolean(formik.errors.startDate),
                      helperText: formik.touched.startDate && formik.errors.startDate && <>{formik.errors.startDate}</>,
                    },
                  }}
                />

                <DatePicker
                  name='endDate'
                  label='End Date'
                  value={formik.values.endDate}
                  onChange={(newValue) => {
                    formik.setFieldValue('endDate', newValue);
                    formik.setFieldTouched('endDate', true);
                  }}
                  format={shortDateFormat}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: formik.touched.endDate && Boolean(formik.errors.endDate),
                      helperText: formik.touched.startDate && formik.errors.endDate && <>{formik.errors.endDate}</>,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </Paper>
      </CustomModal>
    </>
  );
}
